/**
 * Referral Partners Manager - Admin Component
 * Manage, delete, and resend welcome emails to referral partners
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Users, Trash2, Search, RefreshCw, DollarSign, UserCheck, Mail, Send, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReferralPartnersManager = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);
  const [sendingLinkUpdate, setSendingLinkUpdate] = useState(false);
  const [fixingLinks, setFixingLinks] = useState(false);
  
  // Email Results State
  const [showEmailResults, setShowEmailResults] = useState(false);
  const [emailResults, setEmailResults] = useState(null);
  
  // Custom Announcement State
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [sendingAnnouncement, setSendingAnnouncement] = useState(false);
  const [announcementSubject, setAnnouncementSubject] = useState('');
  const [announcementMessage, setAnnouncementMessage] = useState('');

  // Fix All Referral Links - One Click Solution
  const handleFixAllLinks = async () => {
    if (!window.confirm('This will update ALL referral links to use apcestateflow.com. Continue?')) {
      return;
    }
    
    try {
      setFixingLinks(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/admin/fix-all-referral-links`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`✅ Fixed ${response.data.total_fixed} referral links!`, {
          description: `Updated to: ${response.data.correct_domain}`
        });
        // Refresh partners list
        fetchPartners();
      } else {
        toast.error('Failed to fix links');
      }
    } catch (error) {
      console.error('Error fixing links:', error);
      toast.error(error.response?.data?.detail || 'Failed to fix referral links');
    } finally {
      setFixingLinks(false);
    }
  };

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/admin/referral-partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setPartners(response.data.partners || []);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
      toast.error('Failed to load referral partners');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const handleDelete = async (partnerId, partnerName) => {
    try {
      setDeletingId(partnerId);
      const token = localStorage.getItem('access_token');
      const response = await axios.delete(`${API}/admin/referral-partners/${partnerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`Deleted partner: ${partnerName}`);
        setPartners(partners.filter(p => p.id !== partnerId));
      }
    } catch (error) {
      console.error('Error deleting partner:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete partner');
    } finally {
      setDeletingId(null);
    }
  };

  const handleResendEmail = async (partnerId, partnerName, partnerEmail) => {
    try {
      setSendingEmailId(partnerId);
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/admin/referral-partners/${partnerId}/resend-welcome`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`Welcome email sent to ${partnerEmail}`);
      }
    } catch (error) {
      console.error('Error resending email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmailId(null);
    }
  };

  const handleResendAllEmails = async () => {
    try {
      setSendingAll(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/admin/referral-partners/resend-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Store results and show dialog
      setEmailResults({
        type: 'welcome',
        success: response.data.success,
        message: response.data.message,
        sent_count: response.data.sent_count,
        failed_count: response.data.failed_count,
        failed_emails: response.data.failed_emails || []
      });
      setShowEmailResults(true);
      
      if (response.data.success) {
        if (response.data.failed_count === 0) {
          toast.success(`All ${response.data.sent_count} emails sent successfully!`);
        } else {
          toast.warning(`Sent ${response.data.sent_count}, Failed ${response.data.failed_count}`);
        }
      }
    } catch (error) {
      console.error('Error resending all emails:', error);
      setEmailResults({
        type: 'welcome',
        success: false,
        message: error.response?.data?.detail || 'Failed to send emails',
        sent_count: 0,
        failed_count: 0,
        failed_emails: [],
        error: error.response?.data?.detail || error.message
      });
      setShowEmailResults(true);
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSendingAll(false);
    }
  };

  const handleSendLinkUpdateNotification = async () => {
    try {
      setSendingLinkUpdate(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/admin/referral-partners/send-link-update`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`Link update notification sent to ${response.data.sent_count} partners!`);
        if (response.data.failed_count > 0) {
          toast.warning(`Failed to send to ${response.data.failed_count} partners`);
        }
        if (response.data.skipped_count > 0) {
          toast.info(`Skipped ${response.data.skipped_count} test accounts`);
        }
      }
    } catch (error) {
      console.error('Error sending link update:', error);
      toast.error(error.response?.data?.detail || 'Failed to send link update notifications');
    } finally {
      setSendingLinkUpdate(false);
    }
  };

  const handleSendCustomAnnouncement = async () => {
    if (!announcementSubject.trim() || !announcementMessage.trim()) {
      toast.error('Please enter both subject and message');
      return;
    }
    
    try {
      setSendingAnnouncement(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/admin/referral-partners/send-announcement`,
        {
          subject: announcementSubject,
          message: announcementMessage
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`📢 Announcement sent to ${response.data.sent_count} partners!`);
        if (response.data.failed_count > 0) {
          toast.warning(`Failed to send to ${response.data.failed_count} partners`);
        }
        if (response.data.skipped_count > 0) {
          toast.info(`Skipped ${response.data.skipped_count} test accounts`);
        }
        // Reset form and close modal
        setAnnouncementSubject('');
        setAnnouncementMessage('');
        setShowAnnouncementModal(false);
      }
    } catch (error) {
      console.error('Error sending announcement:', error);
      toast.error(error.response?.data?.detail || 'Failed to send announcement');
    } finally {
      setSendingAnnouncement(false);
    }
  };

  // Pre-fill template for referral link update
  const loadLinkUpdateTemplate = () => {
    setAnnouncementSubject('⚠️ Important: Your Referral Link Has Been Updated - Action Required');
    setAnnouncementMessage(`Dear Partner,

We are writing to inform you of an important update to our referral system. Your referral link has been updated to ensure it directs your potential clients to the correct destination.

ACTION REQUIRED:
Please log in to your Referral Dashboard to retrieve your updated referral link and share it with potential clients.

🔗 Login here: https://apcestateflow.com/referral/login

Your referral code remains the same - only the domain has been updated. Your commissions, earnings, and referral history are all intact.

If you have previously shared links with potential clients, we recommend reaching out to them with your updated link to ensure they are properly credited to your account.

Thank you for being a valued partner!

Best regards,
The Estate Flow Team`);
    setShowAnnouncementModal(true);
  };

  const filteredPartners = partners.filter(partner => {
    const searchLower = searchTerm.toLowerCase();
    return (
      partner.email?.toLowerCase().includes(searchLower) ||
      partner.first_name?.toLowerCase().includes(searchLower) ||
      partner.last_name?.toLowerCase().includes(searchLower) ||
      partner.company_name?.toLowerCase().includes(searchLower) ||
      partner.referral_code?.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-emerald-600" />
            Referral Partners ({partners.length})
          </CardTitle>
          <div className="flex gap-2">
            {/* 🔧 FIX ALL LINKS - Emergency Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleFixAllLinks}
              disabled={fixingLinks}
              className="text-red-600 border-red-300 hover:bg-red-50 font-bold"
            >
              {fixingLinks ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Fixing...
                </>
              ) : (
                <>
                  🔧 Fix All Links
                </>
              )}
            </Button>

            {/* Send Custom Announcement Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAnnouncementModal(true)}
              disabled={partners.length === 0}
              className="text-purple-600 border-purple-200 hover:bg-purple-50"
            >
              <Mail className="h-4 w-4 mr-2" />
              📢 Send Announcement
            </Button>

            {/* Quick Link Update Template Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={loadLinkUpdateTemplate}
              disabled={partners.length === 0}
              className="text-amber-600 border-amber-200 hover:bg-amber-50"
            >
              🔗 Link Update Template
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={sendingAll || partners.length === 0}
                  className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                >
                  <Send className={`h-4 w-4 mr-2 ${sendingAll ? 'animate-pulse' : ''}`} />
                  {sendingAll ? 'Sending...' : 'Resend All Emails'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Resend Welcome Emails to All Partners?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will send the welcome email to all <strong>{partners.length}</strong> referral partners.
                    <br /><br />
                    This is useful if partners didn't receive their initial welcome email.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleResendAllEmails}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    Send to All Partners
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPartners}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Search */}
        <div className="flex items-center gap-2 mb-4">
          <Search className="h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name, email, company, or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-sm"
          />
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading partners...</div>
        ) : filteredPartners.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {searchTerm ? 'No partners match your search' : 'No referral partners found'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Partner</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Referral Code</TableHead>
                  <TableHead>Referral URL</TableHead>
                  <TableHead>Referrals</TableHead>
                  <TableHead>Earnings</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPartners.map((partner) => (
                  <TableRow key={partner.id}>
                    <TableCell className="font-medium">
                      {partner.first_name} {partner.last_name}
                      {partner.company_name && (
                        <div className="text-xs text-gray-500">{partner.company_name}</div>
                      )}
                    </TableCell>
                    <TableCell>{partner.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {partner.referrer_type?.replace('_', ' ') || 'Unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {partner.referral_code || 'N/A'}
                      </code>
                    </TableCell>
                    <TableCell>
                      {partner.referral_code ? (
                        <div className="flex items-center gap-1">
                          <a 
                            href={partner.referral_link || `https://apcestateflow.com/signup?ref=${partner.referral_code}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`text-xs max-w-[200px] truncate block ${
                              partner.referral_link?.includes('apcestateflow.com') 
                                ? 'text-green-600 hover:text-green-800' 
                                : 'text-red-600 hover:text-red-800 font-bold'
                            }`}
                            title={partner.referral_link || `https://apcestateflow.com/signup?ref=${partner.referral_code}`}
                          >
                            {partner.referral_link || `https://apcestateflow.com/signup?ref=${partner.referral_code}`}
                          </a>
                          {!partner.referral_link?.includes('apcestateflow.com') && partner.referral_link && (
                            <span className="text-red-500 text-xs">⚠️</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">N/A</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <UserCheck className="h-4 w-4 text-blue-500" />
                        {partner.stats?.total_referrals || 0}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-green-600 font-medium">
                        <DollarSign className="h-4 w-4" />
                        {(partner.stats?.total_earnings || 0).toFixed(2)}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {formatDate(partner.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Resend Email Button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleResendEmail(partner.id, `${partner.first_name} ${partner.last_name}`, partner.email)}
                          disabled={sendingEmailId === partner.id}
                          title="Resend welcome email"
                          className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                        >
                          <Mail className={`h-4 w-4 ${sendingEmailId === partner.id ? 'animate-pulse' : ''}`} />
                        </Button>
                        
                        {/* Delete Button */}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="destructive"
                              size="sm"
                              disabled={deletingId === partner.id}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Referral Partner?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete <strong>{partner.first_name} {partner.last_name}</strong> ({partner.email})?
                                <br /><br />
                                This will:
                                <ul className="list-disc ml-5 mt-2">
                                  <li>Remove their account</li>
                                  <li>Delete their referral code</li>
                                  <li>They will no longer be able to login</li>
                                </ul>
                                <br />
                                This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(partner.id, `${partner.first_name} ${partner.last_name}`)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete Partner
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Summary */}
        {partners.length > 0 && (
          <div className="mt-4 pt-4 border-t flex gap-6 text-sm text-gray-600">
            <div>
              <strong>Total Partners:</strong> {partners.length}
            </div>
            <div>
              <strong>Total Referrals:</strong> {partners.reduce((sum, p) => sum + (p.stats?.total_referrals || 0), 0)}
            </div>
            <div>
              <strong>Total Earnings:</strong> ${partners.reduce((sum, p) => sum + (p.stats?.total_earnings || 0), 0).toFixed(2)}
            </div>
          </div>
        )}

        {/* Custom Announcement Modal */}
        <AlertDialog open={showAnnouncementModal} onOpenChange={setShowAnnouncementModal}>
          <AlertDialogContent className="max-w-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-purple-700">
                <Mail className="h-5 w-5" />
                📢 Send Announcement to All Partners
              </AlertDialogTitle>
              <AlertDialogDescription>
                Compose a custom announcement to send to all real referral partners. Test accounts will be automatically skipped.
              </AlertDialogDescription>
            </AlertDialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Subject *
                </label>
                <Input
                  placeholder="Enter email subject..."
                  value={announcementSubject}
                  onChange={(e) => setAnnouncementSubject(e.target.value)}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message *
                </label>
                <textarea
                  placeholder="Enter your announcement message...

You can use these placeholders:
{first_name} - Partner's first name
{last_name} - Partner's last name
{referral_code} - Partner's referral code
{referral_link} - Partner's full referral link"
                  value={announcementMessage}
                  onChange={(e) => setAnnouncementMessage(e.target.value)}
                  className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />
              </div>
              
              <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
                <p className="text-sm text-purple-700">
                  <strong>Note:</strong> This will be sent to <strong>{partners.filter(p => {
                    const email = (p.email || '').toLowerCase();
                    const testPatterns = ['@test.com', '@example.com', '@test.', 'test@', 'healthcheck@', 'audit'];
                    return !testPatterns.some(pattern => email.includes(pattern));
                  }).length}</strong> real partners (excluding test accounts).
                </p>
              </div>
            </div>
            
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => {
                setAnnouncementSubject('');
                setAnnouncementMessage('');
              }}>
                Cancel
              </AlertDialogCancel>
              <Button
                onClick={handleSendCustomAnnouncement}
                disabled={sendingAnnouncement || !announcementSubject.trim() || !announcementMessage.trim()}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {sendingAnnouncement ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send to All Partners
                  </>
                )}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Email Results Dialog */}
        <Dialog open={showEmailResults} onOpenChange={setShowEmailResults}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {emailResults?.failed_count === 0 ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : emailResults?.sent_count > 0 ? (
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                Email Send Results
              </DialogTitle>
              <DialogDescription>
                {emailResults?.type === 'welcome' ? 'Welcome Email' : 'Email'} sending completed
              </DialogDescription>
            </DialogHeader>
            
            {emailResults && (
              <div className="space-y-4 py-4">
                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-700">{emailResults.sent_count}</div>
                    <div className="text-sm text-green-600">Sent Successfully</div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-red-700">{emailResults.failed_count}</div>
                    <div className="text-sm text-red-600">Failed</div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-blue-700">
                      {emailResults.sent_count + emailResults.failed_count}
                    </div>
                    <div className="text-sm text-blue-600">Total Processed</div>
                  </div>
                </div>

                {/* Success Rate */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Success Rate</span>
                    <span className="font-bold">
                      {((emailResults.sent_count / (emailResults.sent_count + emailResults.failed_count)) * 100 || 0).toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-green-600 h-3 rounded-full transition-all duration-500"
                      style={{ 
                        width: `${(emailResults.sent_count / (emailResults.sent_count + emailResults.failed_count)) * 100 || 0}%` 
                      }}
                    />
                  </div>
                </div>

                {/* Failed Emails List */}
                {emailResults.failed_emails && emailResults.failed_emails.length > 0 && (
                  <div className="border border-red-200 rounded-lg overflow-hidden">
                    <div className="bg-red-50 px-4 py-2 border-b border-red-200">
                      <h4 className="font-semibold text-red-700 flex items-center gap-2">
                        <XCircle className="h-4 w-4" />
                        Failed Emails ({emailResults.failed_emails.length})
                      </h4>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-red-50 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left text-red-700">#</th>
                            <th className="px-4 py-2 text-left text-red-700">Email / Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {emailResults.failed_emails.map((email, index) => (
                            <tr key={index} className="border-t border-red-100 hover:bg-red-25">
                              <td className="px-4 py-2 text-gray-500">{index + 1}</td>
                              <td className="px-4 py-2 font-mono text-xs text-red-600 break-all">
                                {email}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Error Message if present */}
                {emailResults.error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-semibold text-red-700 mb-2">Error Details</h4>
                    <p className="text-sm text-red-600 font-mono">{emailResults.error}</p>
                  </div>
                )}

                {/* Tips if there are failures */}
                {emailResults.failed_count > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-700 mb-2">💡 Troubleshooting Tips</h4>
                    <ul className="text-sm text-yellow-700 list-disc list-inside space-y-1">
                      <li>Check if the email addresses are valid</li>
                      <li>Test accounts (e.g., @test.com, @example.com) may fail</li>
                      <li>Verify SMTP settings in Credentials tab</li>
                      <li>Check System Health for SMTP status</li>
                    </ul>
                  </div>
                )}

                {/* Close Button */}
                <div className="flex justify-end pt-4">
                  <Button onClick={() => setShowEmailResults(false)}>
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default ReferralPartnersManager;
